import { type MouseEvent, useRef } from 'react';

/**
 * The scrim close of every dialog drawn as a panel inside its scrim, in one
 * place. Put the three handlers on the dialog element whose own box is the
 * scrim (the panel is its child).
 *
 * A click on the scrim itself closes the dialog, unless `blocked` (the
 * caller's Escape rule: mid-send, or a gate up). It closes only when the press
 * AND the release were both on the scrim: the browser fires the click of a
 * drag on the common ancestor, so selecting text and letting go over the
 * scrim, a press on a chip let go beside it (#1048), or a press on the scrim
 * let go inside the panel (#1061 review) all read as a scrim click.
 *
 * `media` is where there is a scrim to click. Left out, every width: the
 * dialogs that are a panel over a scrim at every width (DeleteAccountModal,
 * the wallet modals, HelpTooltip...) say nothing, so a dialog that forgets it
 * still closes on a phone. The two sheets pass SHEET_SCRIM_MEDIA: under it the
 * sheet is the whole screen and there is no scrim, so a tap beside a narrow
 * panel keeps the draft.
 */
export function useScrimClose({
  blocked = false,
  onClose,
  media = null,
}: {
  blocked?: boolean;
  onClose: () => void;
  /** Where there is a scrim to click; left out (or null), every width. */
  media?: string | null;
}): {
  onMouseDownCapture: (e: MouseEvent<HTMLElement>) => void;
  onMouseUpCapture: (e: MouseEvent<HTMLElement>) => void;
  onClick: (e: MouseEvent<HTMLElement>) => void;
} {
  // Where the last press began and ended: on the scrim itself, or inside it.
  // Read in the capture phase, so a descendant that stops its own mousedown
  // or mouseup from bubbling (a chip, a slider) cannot hide them. Null when
  // there was none (a synthetic click, or a tap a browser reports as a click
  // alone): nothing contradicts the click's own target then.
  const press = useRef<'scrim' | 'inside' | null>(null);
  const release = useRef<'scrim' | 'inside' | null>(null);
  const where = (e: MouseEvent<HTMLElement>) =>
    e.target === e.currentTarget ? ('scrim' as const) : ('inside' as const);
  const onMouseDownCapture = (e: MouseEvent<HTMLElement>) => {
    press.current = where(e);
  };
  const onMouseUpCapture = (e: MouseEvent<HTMLElement>) => {
    release.current = where(e);
  };
  const onClick = (e: MouseEvent<HTMLElement>) => {
    const fromInside = press.current === 'inside' || release.current === 'inside';
    press.current = null;
    release.current = null;
    if (e.target !== e.currentTarget || fromInside || blocked) return;
    if (media !== null && !window.matchMedia?.(media).matches) return;
    onClose();
  };
  return { onMouseDownCapture, onMouseUpCapture, onClick };
}
